import React, { useMemo, useState, useEffect, useContext, useRef } from "react";
import { getLocale, shallowEqual, useIntl, useSelector } from "umi";
import PerformanceMetricsTable, { MetricsTableColumn } from "@/pages/dashboard/components/PerformanceMetricsTable";
import SelectShowValueType, { ShowValueType } from "@/pages/dashboard/pages/overview/components/OverviewTable/components/SelectShowValueType";
import MetricsSettings, { defaultStatisticsTypeValue } from "@/pages/dashboard/components/MetricsSettings";
import {
  useStatisticsDataFormat,
  useTableColumns,
  useTableDataFormat,
  useMetricsColumns,
} from "./hooks";
import { LANGUAGE_MAP } from "@/pages/dashboard/_config/constant";
import DimensionAccumulateRow from "@/pages/dashboard/components/DimensionAccumulateRow";
import useData from "./useData";
import { MetricsSettingsProp, TableProps } from "./types";
import { useSelectArrObj } from "@/pages/dashboard/utils/customHook";
import { FilterContext } from "@/pages/dashboard/components/PageContainer/context";
import { SortBy } from "tea-component/lib/table/addons";
import { PagingQuery } from "tea-component";
import { Option } from "@/pages/dashboard/components/DownloadFormatModal/types";
import { ComponentMetricsItemType } from "@/pages/dashboard/components/PageContainer/type";
import { prepareMetricsDataSource, resolveAndSelectName, resolveConfigString } from "@/pages/dashboard/utils/metricUtils";

import "@/pages/dashboard/assets/scss/page/Overlay/components/tableConext.scss";
import { handleAccumulatedMetrics, handleMetricsKeyNotByDate, getMultiSortParams } from "@/pages/dashboard/components/OverallTableCo/utils";
import { isRateSort } from "@/pages/dashboard/components/PerformanceMetricsTable/components/_utils";
import { getExplainVal } from "@/pages/dashboard/utils/getExplainVal";
import { stringToFunction } from "@/pages/dashboard/utils/stringToJson";
import classNames from "classnames";
import AIAnalysisButton from "@/pages/dashboard/components/AIAnalysisButton";
import { buildDimensionMetricsAndAppendToMap } from "@/pages/dashboard/utils/dimensionMetricsUtils";

export default function Table(props: TableProps) {
  const defaultTableConfig = {
    hideMetricsSettings: false, hideSelectShowValueType: false, defaultShowValueType: ''
  };
  const { config, style, otherParams = {}, filterPath, logConfig} = props || {};
  const timeZone = parseInt(props?.data?.time_zone || '', 10) || 0;
  const {
    hideMetricsSettings = false,
    showDimList = true,
    hideSelectShowValueType = false,
    defaultShowValueType = '',
    title: configTitle,
    hideExplain = false,
    isShowProgressBar = true,
    showTypeOptions,
    showModalData,
    showModalDataStat,
    isCheckAllAccumulated,
    dateDisabled = false, // overview宽表默认开启取消分日期
    defaultStatisticsType,
    dateColumnConfig,
    accumulateDefault,
    dimensionsFromFilter = false,
    accumulateColumnConfig,
    requiredMetricCountToDownload,
    defaultFileType,
  } = config?.tableConfig || defaultTableConfig;

  // const {metricsInfo: metricsInfoData} = props?.data || {};
  const propsData = props?.data;
  const { componentMetrics: propsComponentMetrics, cutomColumnsBeforeDim = true, customColumns, defaultSort = [], isSingleDate = true, componentMetricsConfig, lockColumns } = config;
  const locale = getLocale();

  const isEn = locale === 'en-US';
  const contextObj = useContext(FilterContext);
  const { filterVal, filterLoading } = contextObj || {};
  const { timeType = 'Day' } = filterVal || {};
  const explainInfo = useSelector((state: any) => state.dashboard?.explainInfo, shallowEqual);
  const { formatMessage } = useIntl();
  const [columns, setColumns] = useState<any[]>([]);
  const defaultValueType = 'value_only';
  const [selectType, setSelectType] = useState<ShowValueType>(defaultShowValueType || defaultValueType);
  const [showStatistic, setShowStatistic] = useState<string[]>(defaultStatisticsType || defaultStatisticsTypeValue);
  const [accumulated, setAccumulated] = useState<string[]>(['date']);
  const [isCallingRateSortApi, setIsCallingRateSortApi] = useState<boolean>(false);

  const columnsRef = useRef<unknown[]>([]);

  /**
   * 根据 tableMetric 配置筛选并排序指标列表
   * @param metrics 原始指标配置数组
   * @param tableMetric 需要筛选的指标 key 数组（决定顺序）
   * @returns 按 tableMetric 顺序排列的指标数组
   */
  const filterComponentMetricsByTableMetric = (
    metrics: ComponentMetricsItemType[],
    tableMetric?: string[]
  ): ComponentMetricsItemType[] => {
    // 未配置 tableMetric 时,返回全部指标
    if (!tableMetric || tableMetric.length === 0) {
      return metrics;
    }

    // 创建 metricKey 到指标配置的映射
    const metricsMap = new Map<string, ComponentMetricsItemType>();
    metrics.forEach((metric) => {
      const metricKey = metric.metricKey || metric.reqMetricKey;
      metricsMap.set(metricKey, metric);
    });

    // 按照 tableMetric 的顺序返回指标
    return tableMetric
      .map((key) => metricsMap.get(key))
      .filter((metric): metric is ComponentMetricsItemType => metric !== undefined);
  };

  const { title, componentMetrics } = useMemo(() => {
    // title兼容旧国际化方式
    let title = formatMessage({ id: "overview_Overall_Performance" });
    if (configTitle) {
      if (typeof configTitle === 'object') {
        title = isEn ? configTitle?.titleEn : configTitle?.titleCn;
      } else {
        title = formatMessage({ id: configTitle });
      }
    }

    // 获取基础指标列表
    const baseMetrics = componentMetricsConfig ? componentMetricsConfig?.[timeType] : propsComponentMetrics;
    
    // 根据 tableMetric 配置筛选指标
    const filteredMetrics = filterComponentMetricsByTableMetric(baseMetrics, propsData?.tableMetric);

    return {
      title,
      componentMetrics: filteredMetrics
    };
  }, [configTitle, formatMessage, isEn, propsComponentMetrics, componentMetricsConfig, timeType, propsData?.tableMetric]);

  // 取消by date分日期操作
  const { hasDate, isAccumulated, metricsList } = useMemo(() => {
    const { hasDate = true, isAccumulated = false, metricsList = componentMetrics } = handleAccumulatedMetrics({ filterVal, isHaveDiff: isSingleDate, componentMetrics, accumulated });
    return {
      hasDate,
      isAccumulated,
      metricsList,
    };
  }, [accumulated, componentMetrics, filterVal, isSingleDate]);

  const compData = useData(
    filterVal,
    filterLoading,
    selectType,
    {
      ...config,
      lockColumns,
      columns,
    },
    isAccumulated ? metricsList : componentMetrics,
    otherParams,
    timeZone
  );

  const {
    loading, isError, paging, setPaging, sort, setSort,
    total, tableData, statisticsData, filterValueSort, accumulateOldValueRef,
    getRequestParams, getData, downLoadData, frontLogicalFlag, overLimitBarTriggerKey
  } = compData || {};

  // 当有页面级配置的时候走配置，没有的时候不走配置
  const newPath = filterPath?.startsWith('/') ? filterPath?.slice(1) : filterPath;
  const dimOptions = useSelectArrObj(filterVal, false, false, false, newPath);
  const [separatedOptions, setSeparatedOptions] = useState([]);

  const COLUMN_CONFIG = useMetricsColumns(isAccumulated ? metricsList : componentMetrics || []);
  const [showMetrics, setShowMetrics] = useState(Object.keys(COLUMN_CONFIG));

  const { DATE_COLUMN, getAccumulateColumns, getMetricColumns, getCustomColumns } = useTableColumns({ isAccumulated, hasDate, timeType, dateColumnConfig, accumulateColumnConfig });

  // 稳定化 COLUMN_CONFIG 的 key 列表，用于依赖比较
  const columnConfigKeysStr = Object.keys(COLUMN_CONFIG).join(',');

  useEffect(() => {
    // 当单日取消分日期时是没有sdlw的，设置回默认
    if (isAccumulated && selectType === 'sdlw') {
      setSelectType(defaultValueType);
    }

    // 当指标配置变化（页面切换/timeType变化）或累计状态变化时，基于最新的 COLUMN_CONFIG 重置 showMetrics
    const newKeys = Object.keys(COLUMN_CONFIG);
    setShowMetrics(handleMetricsKeyNotByDate(componentMetrics, newKeys, isAccumulated, timeType));
  }, [columnConfigKeysStr, isAccumulated, timeType]);

  useEffect(() => {
    const SEPARATED_OPTIONS = dimOptions?.filter((i: { groupKey: string; }) => i?.groupKey === 'separated');
    SEPARATED_OPTIONS.unshift({
      groupKey: 'separated',
      value: 'date',
      text: formatMessage({ id: 'common_by_Date' }),
      disabled: dateDisabled,
    });

    // network_type 仅在当前表格组件使用，不放入公共 hooks
    if (filterVal?.network_type) {
      SEPARATED_OPTIONS.push({
        groupKey: 'separated',
        value: 'network_type',
        text: formatMessage({ id: 'common_by_network_type' }),
      });
    }

    setSeparatedOptions(SEPARATED_OPTIONS);
  }, [dimOptions]);

  useEffect(() => {
    if (isCheckAllAccumulated) {
      const dimList = separatedOptions.map(item => item.value);
      setAccumulated(dimList);
      if (accumulateOldValueRef.current) {
        accumulateOldValueRef.current = dimList;
      };
    };
  }, [separatedOptions]);

  useEffect(() => {
    if(accumulateDefault) {
      accumulateOldValueRef.current = accumulateDefault;
      setAccumulated(accumulateDefault);
    }
   
  }, [accumulateDefault]);

  // 当 dimensionsFromFilter 开启且筛选器提供 dimensions 时，同步到宽表维度列
  // 与 DimensionAccumulateRow 共享同一 accumulated，filterVal 有值则优先使用
  // 注意：filterDimensions 为空数组 [] 时也需同步，否则 accumulated 保持 accumulateDefault（含 date），导致 isAccumulated 始终为 false
  // pv_threshold 来自事件筛选器，非维度选择器，需始终从 accumulateDefault 补充以展示列
  useEffect(() => {
    if (!dimensionsFromFilter) return;
    const filterDimensions = filterVal?.dimensions ?? filterVal?.filters?.dimensions;
    if (Array.isArray(filterDimensions)) {
      const nextAccumulated = filterDimensions.length > 0 ? [...filterDimensions] : [];
      if (accumulateDefault?.includes('pv_threshold') && !nextAccumulated.includes('pv_threshold')) {
        nextAccumulated.push('pv_threshold');
      }
      accumulateOldValueRef.current = nextAccumulated;
      setAccumulated(nextAccumulated);
    }
  }, [dimensionsFromFilter, filterVal?.dimensions, filterVal?.filters?.dimensions, accumulateDefault]);

  const metricsData = useMemo<Record<string, any>>(() => {
    return prepareMetricsDataSource(propsData?.metricsInfo);
  }, [propsData?.metricsInfo]);

  useEffect(() => {
    // 日期维度
    let columns = [DATE_COLUMN];
    // 自定义前置
    if (cutomColumnsBeforeDim && Array.isArray(customColumns)) {
      columns.push(...getCustomColumns(customColumns, explainInfo));
    }
    // 筛选器维度
    columns.push(...getAccumulateColumns(accumulateOldValueRef?.current));
    // 自定义后置
    if (!cutomColumnsBeforeDim && Array.isArray(customColumns)) {
      columns.push(...getCustomColumns(customColumns, explainInfo));
    }
    // 指标
    columns.push(...getMetricColumns(showMetrics, COLUMN_CONFIG, explainInfo, selectType, hideExplain, metricsData));

    // 配置锁住列
    if (Array.isArray(lockColumns) && lockColumns?.length > 0) {
      columns = columns?.map(item => {
        const lockColumn = lockColumns?.find(col => col?.key === item?.key);
        if (lockColumn) {
          const dimList = accumulateOldValueRef.current || [];
          const {
            key,
            lockTipEn,
            lockTipCn,
            unlockTipEn,
            unlockTipCn,
            lockRainbowKey = '',
            unlockRainbowKey = '',
            defaultLock,
            showDimList
          } = lockColumn || {};
          const oldLockColumn = columnsRef.current?.find(c => c?.key === key) || {};
          return {
            ...item,
            lock: oldLockColumn?.lock ?? defaultLock,
            hasLock: showDimList ? dimList?.some(dim => showDimList?.includes(dim)) : defaultLock,
            lockTip: getExplainVal(explainInfo, lockRainbowKey, locale, lockTipEn, lockTipCn),
            unlockTip: getExplainVal(explainInfo, unlockRainbowKey, locale, unlockTipEn, unlockTipCn),
          };
        }
        return item;
      });
      columnsRef.current = columns;
    }

    setColumns(columns);
  }, [accumulateOldValueRef.current, selectType, timeType, showMetrics, locale, contextObj, metricsData, columnConfigKeysStr]);

  // 表格数据
  const records = [
    ...useTableDataFormat({
      tableData,
      metricsInfo: COLUMN_CONFIG,
      timeType: filterVal.timeType,
      metricsData,
    }),
    ...useStatisticsDataFormat({
      statisticsData,
      metricsInfo: COLUMN_CONFIG,
      showStatistic,
      isCopyData: false,
      metricsData,
    }),
  ];

  const copyData = [
    ...useTableDataFormat({
      tableData,
      metricsInfo: COLUMN_CONFIG,
      timeType: filterVal.timeType,
      isCopyData: true,
      metricsData,
    }),
    ...useStatisticsDataFormat({
      statisticsData,
      metricsInfo: COLUMN_CONFIG,
      showStatistic,
      isCopyData: true,
      metricsData,
    }),
  ];

  // 下载弹窗指标options - 兼容模板字符串
  const metricOptions: Option[] = useMemo(() => {
    return Object.keys(COLUMN_CONFIG).map((key) => {
      const config = COLUMN_CONFIG[key]?.metricsConfig;
      // 兼容模板字符串格式，如 "{{metricsInfo.total_users.en}}"
      const name = resolveAndSelectName(isEn, config?.enName, config?.cnName, propsData?.metricsInfo) || '';
      return ({ value: key, text: name });
    });
  }, [COLUMN_CONFIG, isEn, propsData?.metricsInfo]);

  const onSortChange = (sorts: SortBy[] = []) => {
    // 排序
    let sort = sorts;
    if (sort?.length === 0) {
      // 恢复默认排序时，优先取自定义
      sort = defaultSort;
    }
    setSort(sort);
    // 发起请求
    const params = getRequestParams(filterValueSort?.current?.paging, sort, filterValueSort?.current?.filterValue, {
      ...otherParams,
      multi_sort: getMultiSortParams(lockColumns, columnsRef.current, sort, selectType, true)
    });
    // forceUpdate()
    getData(params);
    setTimeout(() => {
      setIsCallingRateSortApi(false);
    }, 0);
  };

  const onSortChangeRef = useRef<(sorts?: SortBy[]) => void>(() => { });
  onSortChangeRef.current = onSortChange;

  const getExtractMetrics = (configMetrisArr: ComponentMetricsItemType[], metricsArr: any[]) => {
    return configMetrisArr.filter(item => metricsArr.includes(item.reqMetricKey));
  };

  const changeLock = (column: MetricsTableColumn) => {
    const newColumns = (columns || [])?.map(item => {
      if (item?.key === column?.key) {
        return {
          ...item,
          lock: !column?.lock
        };
      }
      return item;
    });
    columnsRef.current = newColumns;
    setColumns(newColumns);
    const params = getRequestParams(filterValueSort?.current?.paging, sort, filterValueSort?.current?.filterValue, {
      ...otherParams,
      multi_sort: getMultiSortParams(lockColumns, columnsRef.current, sort, selectType, true)
    });
    getData(params);
  };

  const handleDownload = () => { };

  return (
    <div style={style || {}}>
      <PerformanceMetricsTable
        title={title}
        // className='overall-conext'
        columns={columns}
        records={records}
        copyConfig={
          {
            copyData: copyData
          }
        }
        onLockChange={changeLock}
        hasLockColumns={Array.isArray(lockColumns) && lockColumns?.length > 0}
        selectType={selectType}
        timeType={filterVal?.timeType}
        sorting={{
          defaultSorts: sort,
          onSortChange: onSortChange,

        }}
        paging={{
          pageIndex: paging.pageIndex,
          pageSize: paging.pageSize,
          recordCount: total,
          pageSizeOptions: [10, 20, 30, 40, 50, 100, 200],
          onPagingChange(query: PagingQuery) {
            // 切换分页
            setPaging(query);
            // 发起请求
            const params = getRequestParams(query, sort, filterValueSort?.current?.filterValue, {
              ...otherParams,
              multi_sort: getMultiSortParams(lockColumns, columnsRef.current, sort, selectType, true)
            });
            getData(params);
          }
        }}
        topTip={{
          left: (<div className={classNames('overall-conext-margin js-overview-table-dimension', {'dbd-table-dimension-padding-0': !showDimList && hideMetricsSettings})}>
            {
              showDimList && <DimensionAccumulateRow
                options={separatedOptions}
                value={accumulated}
                onChange={(value: string[]) => {
                  setAccumulated(value);
                  if (accumulateOldValueRef?.current?.toString() !== value?.toString()) {
                    accumulateOldValueRef.current = value;
                    // 重置分页和排序 (不把重置过程写到 getRequestParams中, 是想要保证方法的独立性, 不要有多种作用混杂在一起)
                    setPaging({ pageIndex: 1, pageSize: paging.pageSize });
                    setSort(defaultSort);
                    // 发起请求
                    const { isAccumulated, metricsList } = handleAccumulatedMetrics({ filterVal, isHaveDiff: isSingleDate, componentMetrics, accumulated: value });
                    const list = isAccumulated ? metricsList : componentMetrics;
                    const metrics = list?.map((item: { reqMetricKey: any; }) => item?.reqMetricKey) || [];
                    const currentColumns = columnsRef.current?.filter(item => (value?.includes(item?.key) && item?.isDimColumn) || !item?.isDimColumn)?.map(item => {
                      if (Object.hasOwn(item, 'hasLock')) {
                        const { showDimList, defaultLock } = lockColumns?.find(col => col?.key === item?.key) || {};
                        return { ...item, hasLock: showDimList ? value?.some(dim => showDimList?.includes(dim)) : defaultLock };
                      }
                      return item;
                    });
                    // 取消分日期 metrics指标参数处理
                    const params = getRequestParams({ pageIndex: 1, pageSize: paging.pageSize }, defaultSort, filterValueSort?.current?.filterValue, {
                      ...otherParams,
                      metrics,
                      multi_sort: getMultiSortParams(lockColumns, currentColumns, defaultSort, selectType, true)
                    });
                    getData(params);
                  }
                }}
              />
            }
            {
              !hideMetricsSettings &&
              <SelectShowValueType
                timeType={filterVal.timeType}
                value={selectType}
                onChange={(value) => {
                  setSelectType(value as ShowValueType);
                  if (isRateSort(sort)) {
                    setIsCallingRateSortApi(true);
                    setTimeout(() => {
                      if (sort?.[0].by.includes(value))
                        onSortChangeRef.current(value === 'value_only' ? defaultSort : sort);
                    }, 0);
                  }
                }}
                isShowDod
                isShowSdlw={!isAccumulated} // 单天不分日期时，不展示sdlw
              />
            }
          </div>),
          right: (
            <>
              <AIAnalysisButton componentKey={(config?.tableConfig as any)?.aiAnalysisKey || 'OverviewTableCo'} pageId={logConfig?.pageId} agentId={(config?.tableConfig as any)?.aiAnalysisAgentId}/>
              {!hideSelectShowValueType &&
                <MetricsSettings
                  onConfirm={({ metricValues, statisticsType }: MetricsSettingsProp) => {
                    setShowStatistic(statisticsType);
                    setShowMetrics(metricValues);
                  }}
                  disabledOptions={dateDisabled ? [{ value: 'date', text: formatMessage({ id: 'common_date' }) }] : []}
                  metricOptions={metricOptions}
                  showMetrics={showMetrics}
                  showTypeOptions={showTypeOptions}
                  defaultStatisticsType={defaultStatisticsType}
                />
              }
            </>
          )
        }}
        onResizeEnd={(columns) => setColumns(columns)}
        isShowProgressBar={isShowProgressBar}
        loading={loading || isCallingRateSortApi}
        isError={isError}
        downloadConfig={{
          defaultValue: showMetrics,
          onDownload: ({ isDownloadStatistics, metrics, dataType, fileFormat }) => {
            const metricNameArr = getExtractMetrics(isAccumulated ? metricsList : componentMetrics || [], metrics);
            // 后续下载要给后端传递对应指标的中英文名称，此处提前处理，先注释掉
            const metricsMap: { [key: string]: { en: string; zh: string; type: any; downLoadFormatRule?: string | number; }; } = {};
            metricNameArr?.forEach(item => {
              // 兼容模板字符串格式
              const enName = resolveConfigString(item?.enName, propsData?.metricsInfo) || '';
              const cnName = resolveConfigString(item?.cnName, propsData?.metricsInfo) || '';
              metricsMap[item?.reqMetricKey] = {
                en: enName || '',
                zh: cnName || '',
                type: item?.downLoadFormatRule || item?.type || 'int', // 确保有默认值，向后兼容
              };
            });

            // dimension_metrics 按页面 columns 实际顺序提取（isDimColumn 且排除 date），并把维度中英文补到 metricsMap
            const currentColumns = columnsRef.current?.length ? columnsRef.current : columns;
            const dimensionMetrics = buildDimensionMetricsAndAppendToMap(
              currentColumns as any[],
              metricsMap,
            );

            let params = getRequestParams(paging, sort, filterValueSort?.current?.filterValue, {
              metrics,
              metrics_map: metricsMap,
              show_dod: dataType === "show_dod",
              show_sdlw: dataType === "show_sdlw",
              file_format: fileFormat,
              aggregates: isDownloadStatistics ? showStatistic : [],
              multi_sort: getMultiSortParams(lockColumns, columnsRef.current, sort, selectType, true),
              ...(dimensionMetrics.length > 0 ? { dimension_metrics: dimensionMetrics } : {}),
            });
            if (typeof config?.getParams === 'function') {
              params = config.getParams(params);
            }
            if (typeof config?.getParams === 'string') {
              params = stringToFunction?.(config?.getParams)?.(params);
            }
            const api = `${config?.api?.tableDownloadApi}?language=${LANGUAGE_MAP[locale]}`;
            return downLoadData(api, params);
          },
          timeType: filterVal.timeType,
          metricOptions,
          showData: { valueOnly: true, showDod: true, showSdlw: !isAccumulated },
          showModalData,
          showModalDataStat,
          requiredMetricCountToDownload,
          defaultFileType,
        }}
        onDownload={handleDownload}
        frontLogicalFlag={frontLogicalFlag}
        limitCount={config?.tableConfig?.overLimitCount}
        barLimitCount={config?.tableConfig?.overLimitBarCount}
        barDownloadDisabled={config?.tableConfig?.barDownloadDisabled}
        overLimitBarTriggerKey={overLimitBarTriggerKey}
      />

    </div>
  );
}
